import { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Styled confirmation modal for destructive actions.
 *
 * @param {boolean} open - Whether the dialog is visible.
 * @param {string} title - Dialog heading.
 * @param {string} message - Descriptive text.
 * @param {string} confirmLabel - Label for the confirm button (default: "Confirm").
 * @param {boolean} danger - If true, confirm button is red (default: true).
 * @param {Function} onConfirm - Called when confirm is clicked.
 * @param {Function} onCancel - Called when cancel is clicked or Escape is pressed.
 */
export default function ConfirmDialog({
  open,
  title = 'Are you sure?',
  message = '',
  confirmLabel = 'Confirm',
  danger = true,
  onConfirm,
  onCancel,
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onCancel]);

  if (!open) return null;

  return createPortal(
    <div className="wfe-confirm-overlay" onClick={onCancel}>
      <div className="wfe-confirm-dialog" onClick={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        {message && <p>{message}</p>}
        <div className="wfe-confirm-actions">
          <button className="wfe-btn-discard" onClick={onCancel}>Cancel</button>
          <button
            className={danger ? 'wfe-btn-danger' : 'wfe-btn-save'}
            onClick={onConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
